import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft, Plus, Trash2, Pencil, Loader2, ShieldX, Activity, FileText, 
  Save, AlertCircle, RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Archetypes matching the Strategy page
const ARCHETYPES = {
  asset: "Asset Therapeutics",
  platform_internal: "Platform Therapeutics (Internal)",
  techbio_hybrid: "TechBio Hybrid",
  computational: "Computational / Service Platform",
  enabling_platform: "Enabling Technology Platform"
};

// Categories for diagnosis questions
const DIAGNOSIS_CATEGORIES = [
  "Scientific Foundation",
  "Differentiation & Defensibility",
  "Team & Leadership",
  "Market & Commercial",
  "Funding & Financial"
];

// Default diagnosis questions per archetype (same as Strategy.jsx)
const DEFAULT_DIAGNOSIS_QUESTIONS = {
  asset: {
    "Scientific Foundation": [
      "Do you have preclinical data supporting your lead program?",
      "Is your mechanism of action well-understood and validated?",
      "Do you have clear biomarkers for patient selection?"
    ],
    "Differentiation & Defensibility": [
      "Is your approach targeting a validated pathway differently?",
      "Do you have composition of matter patents for your molecules?",
      "Do you have any clinical data showing differentiation?"
    ],
    "Team & Leadership": [
      "Do you have experienced drug hunters on the team?",
      "Does your team include regulatory expertise?",
      "Do you have advisors with relevant clinical trial experience?"
    ],
    "Market & Commercial": [
      "Is there significant unmet need in your target indication?",
      "Are you aware of current standard of care and its limitations?",
      "Have you analyzed competitor pipelines in your space?"
    ],
    "Funding & Financial": [
      "Do you have funding to reach your next value inflection point?",
      "Is your budget realistic for your development plans?",
      "Do you have a clear plan for your next funding round?"
    ]
  },
  platform_internal: {
    "Scientific Foundation": [
      "Is your platform technology validated through peer-reviewed publications?",
      "Do you have clear proof-of-concept data for your core platform?",
      "Can your platform be applied across multiple therapeutic areas?"
    ],
    "Differentiation & Defensibility": [
      "Do you have patents protecting your core platform technology?",
      "Is your platform demonstrably superior to alternatives in key metrics?",
      "Would it take competitors significant time/resources to replicate your approach?"
    ],
    "Team & Leadership": [
      "Does your team include the inventor(s) of the core technology?",
      "Do you have leadership with biotech commercialization experience?",
      "Do you have expertise in business development for platform deals?"
    ],
    "Market & Commercial": [
      "Have you had discussions with potential pharma partners?",
      "Is there clear demand for platform solutions in your focus area?",
      "Do you have a clear strategy for platform vs. internal programs?"
    ],
    "Funding & Financial": [
      "Do you have runway for 18+ months of operations?",
      "Is your burn rate aligned with investor expectations for your stage?",
      "Do you have investors who understand platform business models?"
    ]
  },
  techbio_hybrid: {
    "Scientific Foundation": [
      "Do you have validated science for both platform and programs?",
      "Are your internal programs best-in-class applications of your platform?",
      "Can you demonstrate platform capabilities through internal successes?"
    ],
    "Differentiation & Defensibility": [
      "Is your platform differentiation clear to partners vs. competitors?",
      "Do your internal programs have strong competitive positioning?",
      "Do you have IP protection for both platform and products?"
    ],
    "Team & Leadership": [
      "Do you have leadership experienced in both tech and bio?",
      "Can your team manage parallel partnering and internal programs?",
      "Do you have BD and R&D leadership with complementary skills?"
    ],
    "Market & Commercial": [
      "Are partners interested in your platform capabilities?",
      "Have you identified optimal indications for internal programs?",
      "Is there synergy between partnering and internal development?"
    ],
    "Funding & Financial": [
      "Do you have a financial model balancing both strategies?",
      "Is your funding structure appropriate for dual approach?",
      "Do you have clear milestones for both platform and programs?"
    ]
  },
  computational: {
    "Scientific Foundation": [
      "Are your computational methods validated against experimental data?",
      "Do you have published benchmarks showing your approach works?",
      "Can you demonstrate consistent reproducibility of results?"
    ],
    "Differentiation & Defensibility": [
      "Do your algorithms offer unique capabilities vs. competitors?",
      "Do you have proprietary datasets or methods?",
      "Do you have specialized expertise that's hard to replicate?"
    ],
    "Team & Leadership": [
      "Do you have domain experts in both computation and biology?",
      "Do you have operational leadership with service business experience?",
      "Is your team sized appropriately for current demand?"
    ],
    "Market & Commercial": [
      "Do you have repeat customers using your platform?",
      "Is there growing demand for computational solutions in your area?",
      "Do you have a clear pricing strategy with healthy margins?"
    ],
    "Funding & Financial": [
      "Are you generating recurring revenue?",
      "Is your business model scalable without linear team growth?",
      "Do you have positive unit economics on your services?"
    ]
  },
  enabling_platform: {
    "Scientific Foundation": [
      "Is your enabling technology validated through peer-reviewed studies?",
      "Do you have proof-of-concept data with multiple partners/applications?",
      "Can your technology enable breakthroughs across therapeutic areas?"
    ],
    "Differentiation & Defensibility": [
      "Do you have patents protecting your core enabling technology?",
      "Is your technology demonstrably better than existing approaches?",
      "Do you have trade secrets or know-how that's hard to replicate?"
    ],
    "Team & Leadership": [
      "Does your team include the inventor(s) of the technology?",
      "Do you have leadership experienced in tech licensing/partnerships?",
      "Do you have expertise in scaling platform technology?"
    ],
    "Market & Commercial": [
      "Have you validated demand from potential partners?",
      "Is there a clear path to broad adoption of your technology?",
      "Do you have a clear licensing or partnership strategy?"
    ],
    "Funding & Financial": [
      "Do you have runway for 18+ months of development?",
      "Is your burn rate aligned with platform company expectations?",
      "Do you have investors who understand enabling technology models?"
    ]
  }
};

// Default fields by section
const DEFAULT_FIELDS_BY_SECTION = {
  value_proposition: [
    { key: "core_value", label: "What unique value does your company provide?", type: "text" },
    { key: "problem_solving", label: "What problem are you solving?", type: "text" },
    { key: "target_customers", label: "Who are your primary customers/partners?", type: "text" }
  ],
  competitive: [
    { key: "competitors", label: "Key Competitors", type: "competitor_table" },
    { key: "differentiation", label: "How do you differentiate from competitors?", type: "text" }
  ],
  team: [
    { key: "team_members", label: "Core Team", type: "team_table" },
    { key: "team_gaps", label: "What key hires are you planning?", type: "text" }
  ],
  pipeline: [
    { key: "pipeline_programs", label: "Pipeline Programs", type: "pipeline_table" }
  ],
  development: [
    { key: "milestones", label: "Key Milestones", type: "milestone_table" },
    { key: "development_risks", label: "What are your main development risks?", type: "text" }
  ],
  commercial: [
    { key: "go_to_market", label: "What is your go-to-market strategy?", type: "text" },
    { key: "partnerships", label: "What partnerships are you pursuing?", type: "text" }
  ],
  regulatory: [
    { key: "regulatory_pathway", label: "What is your regulatory pathway?", type: "text" },
    { key: "regulatory_milestones", label: "Key regulatory milestones?", type: "text" }
  ],
  ip: [
    { key: "ip_portfolio", label: "IP Portfolio", type: "ip_table" },
    { key: "ip_strategy", label: "What is your IP strategy going forward?", type: "text" }
  ],
  risk: [
    { key: "key_risks", label: "Key Strategic Risks", type: "risk_table" }
  ],
  funding_strategy: [
    { key: "funding_history", label: "Funding History (Previous rounds / grants)", type: "funding_table" },
    { key: "current_runway", label: "Current runway (months)", type: "text" },
    { key: "target_raise_amount", label: "Target raise amount (£)", type: "currency" },
    { key: "target_runway", label: "Target runway (months)", type: "text" },
    { key: "target_milestone", label: "Target milestone", type: "milestone_select" },
    { key: "milestone_timeline", label: "Target milestone timeline", type: "text" }
  ]
};

// Strategy sections for fields
const STRATEGY_SECTIONS = [
  { id: "value_proposition", title: "Value Proposition" },
  { id: "competitive", title: "Competitive Landscape" },
  { id: "team", title: "Team & Expertise" },
  { id: "pipeline", title: "Pipeline & Programs" },
  { id: "development", title: "Development Strategy" },
  { id: "commercial", title: "Commercial Strategy" },
  { id: "regulatory", title: "Regulatory Strategy" },
  { id: "ip", title: "IP & Protection" },
  { id: "risk", title: "Risk Management" },
  { id: "funding_strategy", title: "Funding Strategy" }
];

// Diagnosis Question Manager Component
function DiagnosisQuestionManager({ questions, setQuestions, deletedDefaults, setDeletedDefaults, loading }) {
  const [selectedArchetype, setSelectedArchetype] = useState("asset");
  const [editDialog, setEditDialog] = useState({ open: false, question: null });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Build merged list: defaults + custom, excluding deleted defaults
  const getMergedQuestions = () => {
    const merged = [];
    const defaultQuestions = DEFAULT_DIAGNOSIS_QUESTIONS[selectedArchetype] || {};
    
    // Add default questions (that aren't deleted and aren't overridden)
    DIAGNOSIS_CATEGORIES.forEach(category => {
      const categoryDefaults = defaultQuestions[category] || [];
      categoryDefaults.forEach((q, idx) => {
        const defaultId = `default_${selectedArchetype}_${category}_${idx}`;
        const isDeleted = deletedDefaults.some(d => d.default_id === defaultId);
        const isOverridden = questions.some(cq => cq.default_id === defaultId);
        
        if (!isDeleted && !isOverridden) {
          merged.push({
            _id: defaultId,
            archetype: selectedArchetype,
            category,
            question: q,
            isDefault: true,
            defaultIndex: idx
          });
        }
      });
    });
    
    // Add custom questions (including overridden defaults)
    questions
      .filter(q => q.archetype === selectedArchetype)
      .forEach(q => merged.push({ ...q, isDefault: false }));
    
    // Sort by category then by default status
    merged.sort((a, b) => {
      const catCompare = DIAGNOSIS_CATEGORIES.indexOf(a.category) - DIAGNOSIS_CATEGORIES.indexOf(b.category);
      if (catCompare !== 0) return catCompare;
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return 0;
    });
    
    return merged;
  };

  const mergedQuestions = getMergedQuestions();

  const handleSave = async () => {
    if (!editDialog.question?.question?.trim()) {
      toast.error("Question text is required");
      return;
    }
    setSaving(true);
    try {
      const q = editDialog.question;
      const isNewCustom = !q._id || q._id.startsWith('default_');
      const url = isNewCustom 
        ? `${BACKEND_URL}/api/admin/strategy-questions`
        : `${BACKEND_URL}/api/admin/strategy-questions/${q._id}`;
      
      const payload = {
        archetype: q.archetype,
        category: q.category,
        question: q.question
      };
      
      // If editing a default, track which default it overrides
      if (q._id?.startsWith('default_')) {
        payload.default_id = q._id;
      }
      
      const response = await fetch(url, {
        method: isNewCustom ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (isNewCustom) {
          setQuestions([...questions, data.question]);
        } else {
          setQuestions(questions.map(existing => existing._id === data.question._id ? data.question : existing));
        }
        toast.success(q._id?.startsWith('default_') ? "Default question customized" : isNewCustom ? "Question added" : "Question updated");
        setEditDialog({ open: false, question: null });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save question");
      }
    } catch (error) {
      toast.error("Failed to save question");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (question) => {
    const isDefault = question.isDefault || question._id?.startsWith('default_');
    setDeleting(question._id);
    
    try {
      if (isDefault && !question.default_id) {
        // Deleting a default question - save to deleted_defaults
        const response = await fetch(`${BACKEND_URL}/api/admin/deleted-defaults`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            type: "diagnosis",
            default_id: question._id,
            archetype: question.archetype,
            category: question.category
          })
        });
        if (response.ok) {
          const data = await response.json();
          setDeletedDefaults([...deletedDefaults, data.deleted]);
          toast.success("Default question hidden");
        } else {
          toast.error("Failed to hide question");
        }
      } else {
        // Deleting a custom question
        const response = await fetch(`${BACKEND_URL}/api/admin/strategy-questions/${question._id}`, {
          method: "DELETE",
          credentials: "include"
        });
        if (response.ok) {
          setQuestions(questions.filter(q => q._id !== question._id));
          toast.success("Question deleted");
        } else {
          toast.error("Failed to delete question");
        }
      }
    } catch {
      toast.error("Failed to delete question");
    } finally {
      setDeleting(null);
    }
  };

  const handleRestore = async (defaultId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/deleted-defaults/${encodeURIComponent(defaultId)}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        setDeletedDefaults(deletedDefaults.filter(d => d.default_id !== defaultId));
        toast.success("Default question restored");
      } else {
        toast.error("Failed to restore question");
      }
    } catch {
      toast.error("Failed to restore question");
    }
  };

  const openAddDialog = () => {
    setEditDialog({
      open: true,
      question: {
        archetype: selectedArchetype,
        category: DIAGNOSIS_CATEGORIES[0],
        question: ""
      }
    });
  };

  const openEditDialog = (question) => {
    setEditDialog({ open: true, question: { ...question } });
  };

  // Get deleted defaults for current archetype
  const deletedForArchetype = deletedDefaults.filter(d => d.archetype === selectedArchetype && d.type === "diagnosis");

  return (
    <Card className="border border-slate-200">
      <CardHeader className="border-b border-slate-100">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-teal-600" />
            Diagnosis Questions
          </CardTitle>
          <div className="flex items-center gap-3">
            <Select value={selectedArchetype} onValueChange={setSelectedArchetype}>
              <SelectTrigger className="w-64" data-testid="archetype-select">
                <SelectValue placeholder="Select archetype" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ARCHETYPES).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={openAddDialog} className="bg-teal-600 hover:bg-teal-700" data-testid="add-diagnosis-question">
              <Plus className="w-4 h-4 mr-2" /> Add Question
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
          </div>
        ) : mergedQuestions.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Activity className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No questions for this archetype</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-48">Category</TableHead>
                  <TableHead>Question</TableHead>
                  <TableHead className="w-24">Type</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mergedQuestions.map((q) => (
                  <TableRow key={q._id} className="hover:bg-slate-50">
                    <TableCell>
                      <Badge variant="outline" className="whitespace-nowrap">{q.category}</Badge>
                    </TableCell>
                    <TableCell className="text-slate-700">{q.question}</TableCell>
                    <TableCell>
                      {q.isDefault ? (
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200">Default</Badge>
                      ) : q.default_id ? (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200">Modified</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 border-green-200">Custom</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-teal-600"
                          onClick={() => openEditDialog(q)}
                          data-testid={`edit-question-${q._id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-red-500"
                          onClick={() => handleDelete(q)}
                          disabled={deleting === q._id}
                          data-testid={`delete-question-${q._id}`}
                        >
                          {deleting === q._id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        
        {/* Deleted defaults section */}
        {deletedForArchetype.length > 0 && (
          <div className="border-t p-4 bg-slate-50">
            <h4 className="text-sm font-medium text-slate-700 mb-2">Hidden Default Questions</h4>
            <div className="space-y-2">
              {deletedForArchetype.map(d => (
                <div key={d.default_id} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                  <span className="text-slate-500">{d.category}: Question #{d.default_id.split('_').pop()}</span>
                  <Button variant="ghost" size="sm" onClick={() => handleRestore(d.default_id)} className="text-teal-600 hover:text-teal-700">
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* Edit/Add Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, question: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editDialog.question?._id?.startsWith('default_') ? "Edit Default Question" : editDialog.question?._id ? "Edit Question" : "Add New Question"}
            </DialogTitle>
            <DialogDescription>
              {editDialog.question?._id?.startsWith('default_') 
                ? "Editing a default will create a custom version that overrides it."
                : "Create or modify a diagnosis question."}
            </DialogDescription>
          </DialogHeader>
          {editDialog.question && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Archetype</Label>
                <Select 
                  value={editDialog.question.archetype} 
                  onValueChange={(v) => setEditDialog({ ...editDialog, question: { ...editDialog.question, archetype: v } })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ARCHETYPES).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={editDialog.question.category} 
                  onValueChange={(v) => setEditDialog({ ...editDialog, question: { ...editDialog.question, category: v } })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIAGNOSIS_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Question</Label>
                <Textarea
                  value={editDialog.question.question}
                  onChange={(e) => setEditDialog({ ...editDialog, question: { ...editDialog.question, question: e.target.value } })}
                  placeholder="Enter the diagnosis question..."
                  rows={3}
                  data-testid="question-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, question: null })}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Strategy Fields Manager Component
function StrategyFieldManager({ fields, setFields, deletedDefaults, setDeletedDefaults, loading }) {
  const [selectedSection, setSelectedSection] = useState("value_proposition");
  const [editDialog, setEditDialog] = useState({ open: false, field: null });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Build merged list: defaults + custom, excluding deleted defaults
  const getMergedFields = () => {
    const merged = [];
    const defaultFields = DEFAULT_FIELDS_BY_SECTION[selectedSection] || [];
    
    // Add default fields (that aren't deleted and aren't overridden)
    defaultFields.forEach((f, idx) => {
      const defaultId = `default_field_${selectedSection}_${f.key}`;
      const isDeleted = deletedDefaults.some(d => d.default_id === defaultId);
      const isOverridden = fields.some(cf => cf.default_id === defaultId || cf.key === f.key);
      
      if (!isDeleted && !isOverridden) {
        merged.push({
          _id: defaultId,
          section: selectedSection,
          key: f.key,
          question: f.label,
          field_type: f.type,
          isDefault: true
        });
      }
    });
    
    // Add custom fields (including overridden defaults)
    fields
      .filter(f => f.section === selectedSection)
      .forEach(f => merged.push({ ...f, isDefault: false }));
    
    return merged;
  };

  const mergedFields = getMergedFields();

  const handleSave = async () => {
    if (!editDialog.field?.question?.trim()) {
      toast.error("Question text is required");
      return;
    }
    if (!editDialog.field?.key?.trim()) {
      toast.error("Field key is required");
      return;
    }
    setSaving(true);
    try {
      const f = editDialog.field;
      const isNewCustom = !f._id || f._id.startsWith('default_');
      const url = isNewCustom 
        ? `${BACKEND_URL}/api/admin/strategy-fields`
        : `${BACKEND_URL}/api/admin/strategy-fields/${f._id}`;
      
      const payload = {
        section: f.section,
        key: f.key,
        question: f.question,
        field_type: f.field_type || "text"
      };
      
      // If editing a default, track which default it overrides
      if (f._id?.startsWith('default_')) {
        payload.default_id = f._id;
      }

      const response = await fetch(url, {
        method: isNewCustom ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (isNewCustom) {
          setFields([...fields, data.field]);
        } else {
          setFields(fields.map(existing => existing._id === data.field._id ? data.field : existing));
        }
        toast.success(f._id?.startsWith('default_') ? "Default field customized" : isNewCustom ? "Field added" : "Field updated");
        setEditDialog({ open: false, field: null });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save field");
      }
    } catch (error) {
      toast.error("Failed to save field");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (field) => {
    const isDefault = field.isDefault || field._id?.startsWith('default_');
    setDeleting(field._id);
    
    try {
      if (isDefault && !field.default_id) {
        // Deleting a default field - save to deleted_defaults
        const response = await fetch(`${BACKEND_URL}/api/admin/deleted-defaults`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            type: "field",
            default_id: field._id,
            section: field.section,
            key: field.key
          })
        });
        if (response.ok) {
          const data = await response.json();
          setDeletedDefaults([...deletedDefaults, data.deleted]);
          toast.success("Default field hidden");
        } else {
          toast.error("Failed to hide field");
        }
      } else {
        // Deleting a custom field
        const response = await fetch(`${BACKEND_URL}/api/admin/strategy-fields/${field._id}`, {
          method: "DELETE",
          credentials: "include"
        });
        if (response.ok) {
          setFields(fields.filter(f => f._id !== field._id));
          toast.success("Field deleted");
        } else {
          toast.error("Failed to delete field");
        }
      }
    } catch {
      toast.error("Failed to delete field");
    } finally {
      setDeleting(null);
    }
  };

  const handleRestore = async (defaultId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/deleted-defaults/${encodeURIComponent(defaultId)}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        setDeletedDefaults(deletedDefaults.filter(d => d.default_id !== defaultId));
        toast.success("Default field restored");
      } else {
        toast.error("Failed to restore field");
      }
    } catch {
      toast.error("Failed to restore field");
    }
  };

  const openAddDialog = () => {
    setEditDialog({
      open: true,
      field: {
        section: selectedSection,
        key: "",
        question: "",
        field_type: "text"
      }
    });
  };

  const openEditDialog = (field) => {
    setEditDialog({ open: true, field: { ...field } });
  };

  // Get deleted defaults for current section
  const deletedForSection = deletedDefaults.filter(d => d.section === selectedSection && d.type === "field");

  return (
    <Card className="border border-slate-200">
      <CardHeader className="border-b border-slate-100">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Strategy Section Questions
          </CardTitle>
          <div className="flex items-center gap-3">
            <Select value={selectedSection} onValueChange={setSelectedSection}>
              <SelectTrigger className="w-56" data-testid="section-select">
                <SelectValue placeholder="Select section" />
              </SelectTrigger>
              <SelectContent>
                {STRATEGY_SECTIONS.map((section) => (
                  <SelectItem key={section.id} value={section.id}>{section.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={openAddDialog} className="bg-blue-600 hover:bg-blue-700" data-testid="add-strategy-field">
              <Plus className="w-4 h-4 mr-2" /> Add Question
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : mergedFields.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No fields for this section</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-32">Key</TableHead>
                  <TableHead>Question</TableHead>
                  <TableHead className="w-24">Field Type</TableHead>
                  <TableHead className="w-24">Source</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mergedFields.map((f) => (
                  <TableRow key={f._id} className="hover:bg-slate-50">
                    <TableCell>
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded">{f.key}</code>
                    </TableCell>
                    <TableCell className="text-slate-700">{f.question}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{f.field_type || "text"}</Badge>
                    </TableCell>
                    <TableCell>
                      {f.isDefault ? (
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200">Default</Badge>
                      ) : f.default_id ? (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200">Modified</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 border-green-200">Custom</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-blue-600"
                          onClick={() => openEditDialog(f)}
                          data-testid={`edit-field-${f._id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-red-500"
                          onClick={() => handleDelete(f)}
                          disabled={deleting === f._id}
                          data-testid={`delete-field-${f._id}`}
                        >
                          {deleting === f._id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        
        {/* Deleted defaults section */}
        {deletedForSection.length > 0 && (
          <div className="border-t p-4 bg-slate-50">
            <h4 className="text-sm font-medium text-slate-700 mb-2">Hidden Default Fields</h4>
            <div className="space-y-2">
              {deletedForSection.map(d => (
                <div key={d.default_id} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                  <span className="text-slate-500">{d.key}</span>
                  <Button variant="ghost" size="sm" onClick={() => handleRestore(d.default_id)} className="text-blue-600 hover:text-blue-700">
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* Edit/Add Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, field: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editDialog.field?._id?.startsWith('default_') ? "Edit Default Field" : editDialog.field?._id ? "Edit Field" : "Add New Field"}
            </DialogTitle>
            <DialogDescription>
              {editDialog.field?._id?.startsWith('default_') 
                ? "Editing a default will create a custom version that overrides it."
                : "Create or modify a strategy field question."}
            </DialogDescription>
          </DialogHeader>
          {editDialog.field && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Section</Label>
                <Select 
                  value={editDialog.field.section} 
                  onValueChange={(v) => setEditDialog({ ...editDialog, field: { ...editDialog.field, section: v } })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STRATEGY_SECTIONS.map((section) => (
                      <SelectItem key={section.id} value={section.id}>{section.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Field Key</Label>
                <Input
                  value={editDialog.field.key}
                  onChange={(e) => setEditDialog({ ...editDialog, field: { ...editDialog.field, key: e.target.value.toLowerCase().replace(/\s+/g, '_') } })}
                  placeholder="e.g., custom_question_1"
                  disabled={editDialog.field._id?.startsWith('default_')}
                  data-testid="field-key-input"
                />
                <p className="text-xs text-slate-500">Unique identifier for storing the answer</p>
              </div>
              <div className="space-y-2">
                <Label>Question</Label>
                <Textarea
                  value={editDialog.field.question}
                  onChange={(e) => setEditDialog({ ...editDialog, field: { ...editDialog.field, question: e.target.value } })}
                  placeholder="Enter the question to display..."
                  rows={3}
                  data-testid="field-question-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Field Type</Label>
                <Select 
                  value={editDialog.field.field_type || "text"} 
                  onValueChange={(v) => setEditDialog({ ...editDialog, field: { ...editDialog.field, field_type: v } })}
                  disabled={editDialog.field._id?.startsWith('default_')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text Area</SelectItem>
                    <SelectItem value="competitor_table">Competitor Table</SelectItem>
                    <SelectItem value="team_table">Team Table</SelectItem>
                    <SelectItem value="pipeline_table">Pipeline Table</SelectItem>
                    <SelectItem value="milestone_table">Milestone Table</SelectItem>
                    <SelectItem value="funding_table">Funding Table</SelectItem>
                    <SelectItem value="ip_table">IP Table</SelectItem>
                    <SelectItem value="risk_table">Risk Table</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, field: null })}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function AdminStrategyQuestions() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [diagnosisQuestions, setDiagnosisQuestions] = useState([]);
  const [strategyFields, setStrategyFields] = useState([]);
  const [deletedDefaults, setDeletedDefaults] = useState([]);
  const [activeTab, setActiveTab] = useState("diagnosis");

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const userRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
      if (!userRes.ok) {
        navigate("/auth");
        return;
      }
      setUser(await userRes.json());

      const adminRes = await fetch(`${BACKEND_URL}/api/admin/check`, { credentials: "include" });
      if (adminRes.ok) {
        const data = await adminRes.json();
        setIsAdmin(data.is_admin);
        if (data.is_admin) {
          fetchData();
        } else {
          setLoading(false);
        }
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    } catch (error) {
      navigate("/auth");
    }
  };

  const fetchData = async () => {
    try {
      const [questionsRes, fieldsRes, deletedRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/strategy-questions`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/strategy-fields`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/admin/deleted-defaults`, { credentials: "include" }).catch(() => ({ ok: false }))
      ]);

      if (questionsRes.ok) {
        const data = await questionsRes.json();
        setDiagnosisQuestions(data.questions || []);
      }

      if (fieldsRes.ok) {
        const data = await fieldsRes.json();
        setStrategyFields(data.fields || []);
      }

      if (deletedRes.ok) {
        const data = await deletedRes.json();
        setDeletedDefaults(data.deleted || []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (isAdmin === null || (isAdmin && loading)) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  // Access denied
  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="max-w-md mx-auto border border-red-200">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <ShieldX className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
              <p className="text-slate-500 mb-6">
                You don't have permission to access the admin panel.
              </p>
              <Button onClick={() => navigate("/dashboard")} className="btn-secondary">
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/admin")} 
            className="rounded-full"
            data-testid="back-to-admin"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="page-title">
              Strategy Questions
            </h1>
            <p className="text-slate-600 mt-1">
              Manage diagnosis questions and strategy section fields
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="border border-blue-200 bg-blue-50/50 mb-6">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How it works</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li><strong>Default questions</strong> (blue badge) are built-in and can be edited or hidden</li>
                <li><strong>Modified questions</strong> (amber badge) are customized versions of defaults</li>
                <li><strong>Custom questions</strong> (green badge) are new questions you've added</li>
                <li>Hidden defaults can be restored from the "Hidden" section at the bottom</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="diagnosis" className="gap-2" data-testid="tab-diagnosis">
              <Activity className="w-4 h-4" />
              Diagnosis Questions
            </TabsTrigger>
            <TabsTrigger value="fields" className="gap-2" data-testid="tab-fields">
              <FileText className="w-4 h-4" />
              Strategy Fields
            </TabsTrigger>
          </TabsList>

          <TabsContent value="diagnosis">
            <DiagnosisQuestionManager 
              questions={diagnosisQuestions} 
              setQuestions={setDiagnosisQuestions}
              deletedDefaults={deletedDefaults}
              setDeletedDefaults={setDeletedDefaults}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="fields">
            <StrategyFieldManager 
              fields={strategyFields} 
              setFields={setStrategyFields}
              deletedDefaults={deletedDefaults}
              setDeletedDefaults={setDeletedDefaults}
              loading={loading}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
